/**
 * System prompt default strings for display in the System Prompts admin page.
 * BedrockConverse-owned prompts load via `bedrockconverse/prompts`.
 * Page-content templates live under PageContent (same Lambda that runs generatePageContent).
 * Guides intentionally resolve from OpenAiFunctions because that Lambda owns guide generation.
 */

/* eslint-disable import/extensions, import/no-restricted-paths -- alias + ?raw */
import shoppingAssistantPromptRaw from 'bedrockconverse/prompts/SHOPPING_ASSISTANT_PROMPT.txt?raw';
import aiSearchPromptRaw from 'bedrockconverse/prompts/AI_MODE_SEARCH_PROMPT.txt?raw';
import dualSearchSystemPromptRaw from 'bedrockconverse/prompts/DUAL_SEARCH_PROMPT.txt?raw';
import orderStatusToolPromptRaw from 'bedrockconverse/prompts/ORDER_STATUS_TOOL_PROMPT.txt?raw';
import exitToolPromptRaw from 'bedrockconverse/prompts/EXIT_TOOL_PROMPT.txt?raw';
import productContentPromptRaw from 'contentgeneration/prompts/PRODUCT_CONTENT_PROMPT.txt?raw';
import categoryContentPromptRaw from 'contentgeneration/prompts/CATEGORY_CONTENT_PROMPT.txt?raw';
import guideTopicsPromptRaw from 'openaifunctions/prompts/GUIDE_TOPICS_PROMPT.txt?raw';
import guideContentPromptRaw from 'openaifunctions/prompts/GUIDE_CONTENT_PROMPT.txt?raw';
import guideContentAppendHybridRaw from 'openaifunctions/prompts/GUIDE_CONTENT_APPEND_HYBRID.txt?raw';
import guideContentAppendSeoHeavyRaw from 'openaifunctions/prompts/GUIDE_CONTENT_APPEND_SEO_HEAVY.txt?raw';
import guideContentAppendGeoHeavyRaw from 'openaifunctions/prompts/GUIDE_CONTENT_APPEND_GEO_HEAVY.txt?raw';
import coreGuideContentPromptRaw from 'openaifunctions/prompts/CORE_GUIDE_CONTENT_PROMPT.txt?raw';
import guideCitationInstructionsRaw from 'openaifunctions/prompts/GUIDE_CITATION_INSTRUCTIONS.txt?raw';
import guideCitationSourcesSuggestPromptRaw from 'openaifunctions/prompts/GUIDE_CITATION_SOURCES_SUGGEST_PROMPT.txt?raw';
import guideEditorialNeutralityInstructionsRaw from 'openaifunctions/prompts/GUIDE_EDITORIAL_NEUTRALITY_INSTRUCTIONS.txt?raw';
import guideHumanVoiceInstructionsRaw from 'openaifunctions/prompts/GUIDE_HUMAN_VOICE_INSTRUCTIONS.txt?raw';
import guideDecisionSupportInstructionsRaw from 'openaifunctions/prompts/GUIDE_DECISION_SUPPORT_INSTRUCTIONS.txt?raw';
import guideScoreSystemPromptRaw from 'openaifunctions/prompts/GUIDE_SCORE_SYSTEM_PROMPT.txt?raw';
import llmsTxtPromptRaw from 'openaifunctions/prompts/LLMS_TXT_PROMPT.txt?raw';
import askAboutProductWidgetPromptRaw from 'bedrockconverse/prompts/ASK_ABOUT_PRODUCT_WIDGET_PROMPT.txt?raw';
/* eslint-enable import/extensions, import/no-restricted-paths */

export const SHOPPING_ASSISTANT_PROMPT = shoppingAssistantPromptRaw as string;
export const AI_MODE_SEARCH_PROMPT = aiSearchPromptRaw as string;
export const DUAL_SEARCH_SYSTEM_PROMPT = dualSearchSystemPromptRaw as string;
export const ORDER_STATUS_TOOL_PROMPT = orderStatusToolPromptRaw as string;
export const EXIT_TOOL_PROMPT = exitToolPromptRaw as string;
export const PRODUCT_CONTENT_PROMPT = productContentPromptRaw as string;
export const CATEGORY_CONTENT_PROMPT = categoryContentPromptRaw as string;
export const GUIDE_TOPICS_PROMPT = guideTopicsPromptRaw as string;
export const GUIDE_CONTENT_PROMPT = guideContentPromptRaw as string;
export const GUIDE_CONTENT_APPEND_HYBRID = guideContentAppendHybridRaw as string;
export const GUIDE_CONTENT_APPEND_SEO_HEAVY = guideContentAppendSeoHeavyRaw as string;
export const GUIDE_CONTENT_APPEND_GEO_HEAVY = guideContentAppendGeoHeavyRaw as string;
export const CORE_GUIDE_CONTENT_PROMPT = coreGuideContentPromptRaw as string;
export const GUIDE_CITATION_INSTRUCTIONS = guideCitationInstructionsRaw as string;
export const GUIDE_CITATION_SOURCES_SUGGEST_PROMPT = guideCitationSourcesSuggestPromptRaw as string;
export const GUIDE_EDITORIAL_NEUTRALITY_INSTRUCTIONS = (
  guideEditorialNeutralityInstructionsRaw as string
);
export const GUIDE_HUMAN_VOICE_INSTRUCTIONS = guideHumanVoiceInstructionsRaw as string;
export const GUIDE_DECISION_SUPPORT_INSTRUCTIONS = (
  guideDecisionSupportInstructionsRaw as string
);
export const GUIDE_SCORE_SYSTEM_PROMPT = guideScoreSystemPromptRaw as string;
export const LLMS_TXT_PROMPT = llmsTxtPromptRaw as string;
export const ASK_ABOUT_PRODUCT_WIDGET_PROMPT = askAboutProductWidgetPromptRaw as string;

/** Default base prompt by System Prompts tab key (for UI display and save-empty-when-default). */
export const SYSTEM_PROMPT_DEFAULTS: Record<string, string> = {
  shoppingAssistant: SHOPPING_ASSISTANT_PROMPT.trim(),
  aiSearch: AI_MODE_SEARCH_PROMPT.trim(),
  dualSearch: DUAL_SEARCH_SYSTEM_PROMPT.trim(),
  orderStatus: ORDER_STATUS_TOOL_PROMPT.trim(),
  exitTool: EXIT_TOOL_PROMPT.trim(),
  productContent: PRODUCT_CONTENT_PROMPT.trim(),
  categoryContent: CATEGORY_CONTENT_PROMPT.trim(),
  guidesTopics: GUIDE_TOPICS_PROMPT.trim(),
  guidesContent: GUIDE_CONTENT_PROMPT.trim(),
  coreGuideContent: CORE_GUIDE_CONTENT_PROMPT.trim(),
  guideCitationInstructions: GUIDE_CITATION_INSTRUCTIONS.trim(),
  guideCitationSourcesSuggest: GUIDE_CITATION_SOURCES_SUGGEST_PROMPT.trim(),
  guideEditorialNeutrality: GUIDE_EDITORIAL_NEUTRALITY_INSTRUCTIONS.trim(),
  guideHumanVoice: GUIDE_HUMAN_VOICE_INSTRUCTIONS.trim(),
  guideDecisionSupport: GUIDE_DECISION_SUPPORT_INSTRUCTIONS.trim(),
  guideScoreSystem: GUIDE_SCORE_SYSTEM_PROMPT.trim(),
  llmsTxt: LLMS_TXT_PROMPT.trim(),
  guidesHybridInstructions: GUIDE_CONTENT_APPEND_HYBRID.trim(),
  guidesSeoHeavyInstructions: GUIDE_CONTENT_APPEND_SEO_HEAVY.trim(),
  guidesGeoHeavyInstructions: GUIDE_CONTENT_APPEND_GEO_HEAVY.trim(),
  askAboutProductWidget: ASK_ABOUT_PRODUCT_WIDGET_PROMPT.trim(),
  portalPromptTemplate: '',
};

/** Normalize for comparison: trim, CRLF → LF, collapse internal whitespace to single space. */
export function normalizeForCompare(s: string): string {
  return s.trim().replace(/\r\n/g, '\n').replace(/\s+/g, ' ');
}

/** Legacy AI Config keys: match default → save empty. Map: field key → default string. */
export const LEGACY_FIELD_DEFAULTS: Record<string, string> = {
  shoppingAssistantPrompt: SYSTEM_PROMPT_DEFAULTS.shoppingAssistant,
  aiModeSearchPrompt: SYSTEM_PROMPT_DEFAULTS.aiSearch,
  dualSearchPrompt: SYSTEM_PROMPT_DEFAULTS.dualSearch,
  dualSearchSystemPromptTemplate: SYSTEM_PROMPT_DEFAULTS.dualSearch,
  productContentPrompt: SYSTEM_PROMPT_DEFAULTS.productContent,
  categoryContentPrompt: SYSTEM_PROMPT_DEFAULTS.categoryContent,
  guidesTopicsPrompt: SYSTEM_PROMPT_DEFAULTS.guidesTopics,
  guidesContentPrompt: SYSTEM_PROMPT_DEFAULTS.guidesContent,
  coreGuideContentPrompt: SYSTEM_PROMPT_DEFAULTS.coreGuideContent,
  citationInstructionsOverride: SYSTEM_PROMPT_DEFAULTS.guideCitationInstructions,
  citationSourcesSuggestPrompt: SYSTEM_PROMPT_DEFAULTS.guideCitationSourcesSuggest,
  editorialInstructionsOverride: SYSTEM_PROMPT_DEFAULTS.guideEditorialNeutrality,
  humanVoiceInstructionsOverride: SYSTEM_PROMPT_DEFAULTS.guideHumanVoice,
  decisionSupportInstructionsOverride: SYSTEM_PROMPT_DEFAULTS.guideDecisionSupport,
  guideScoreSystemPrompt: SYSTEM_PROMPT_DEFAULTS.guideScoreSystem,
  llmsTxtPrompt: SYSTEM_PROMPT_DEFAULTS.llmsTxt,
  askAboutProductWidgetPrompt: SYSTEM_PROMPT_DEFAULTS.askAboutProductWidget,
  orderStatusToolSystemPromptInstructions: SYSTEM_PROMPT_DEFAULTS.orderStatus,
  exitToolSystemPromptInstructions: SYSTEM_PROMPT_DEFAULTS.exitTool,
  clientAIPromptTemplate: '',
};

/** New APPEND fields (sibling substitutes for overrides). Map: field key → default string. */
export const APPEND_FIELD_DEFAULTS: Record<string, string> = {
  shoppingAssistantAppend: SYSTEM_PROMPT_DEFAULTS.shoppingAssistant,
  aiModeSearchAppend: SYSTEM_PROMPT_DEFAULTS.aiSearch,
  dualSearchAppend: SYSTEM_PROMPT_DEFAULTS.dualSearch,
  clientAIAppend: SYSTEM_PROMPT_DEFAULTS.shoppingAssistant,
  orderStatusToolInstructions: SYSTEM_PROMPT_DEFAULTS.orderStatus,
  exitToolInstructions: SYSTEM_PROMPT_DEFAULTS.exitTool,
  productContentInstructions: SYSTEM_PROMPT_DEFAULTS.productContent,
  categoryContentInstructions: SYSTEM_PROMPT_DEFAULTS.categoryContent,
  guidesTopicsPrompt: SYSTEM_PROMPT_DEFAULTS.guidesTopics,
  guidesContentPrompt: SYSTEM_PROMPT_DEFAULTS.guidesContent,
  coreGuideContentPrompt: SYSTEM_PROMPT_DEFAULTS.coreGuideContent,
  guidesHybridInstructions: SYSTEM_PROMPT_DEFAULTS.guidesHybridInstructions,
  guidesSeoHeavyInstructions: SYSTEM_PROMPT_DEFAULTS.guidesSeoHeavyInstructions,
  guidesGeoHeavyInstructions: SYSTEM_PROMPT_DEFAULTS.guidesGeoHeavyInstructions,
};

/** Tab key → legacy override field name
 * (clientAIConfig, or portal root for clientAIPromptTemplate). */
export const LEGACY_FIELD_BY_TAB_KEY: Record<string, string> = {
  shoppingAssistant: 'shoppingAssistantPrompt',
  aiSearch: 'aiModeSearchPrompt',
  dualSearch: 'dualSearchPrompt',
  orderStatus: 'orderStatusToolSystemPromptInstructions',
  exitTool: 'exitToolSystemPromptInstructions',
  productContent: 'productContentPrompt',
  categoryContent: 'categoryContentPrompt',
  guidesTopics: 'guidesTopicsPrompt',
  guidesContent: 'guidesContentPrompt',
  coreGuideContent: 'coreGuideContentPrompt',
  guideCitationInstructions: 'citationInstructionsOverride',
  guideCitationSourcesSuggest: 'citationSourcesSuggestPrompt',
  guideEditorialNeutrality: 'editorialInstructionsOverride',
  guideHumanVoice: 'humanVoiceInstructionsOverride',
  guideDecisionSupport: 'decisionSupportInstructionsOverride',
  guideScoreSystem: 'guideScoreSystemPrompt',
  llmsTxt: 'llmsTxtPrompt',
  askAboutProductWidget: 'askAboutProductWidgetPrompt',
  portalPromptTemplate: 'clientAIPromptTemplate',
};

/** All field keys with "match default →
 * save empty" and their defaults. Used for save normalization. */
export const MATCH_EMPTY_FIELD_DEFAULTS: Record<string, string> = {
  ...LEGACY_FIELD_DEFAULTS,
  ...APPEND_FIELD_DEFAULTS,
};

/** Default string for a field key (legacy or append). Use for match→save empty. */
export function getDefaultForMatchEmpty(key: string): string {
  return MATCH_EMPTY_FIELD_DEFAULTS[key] ?? '';
}

/** Default string by defaultKey (e.g. orderStatus). Placeholder for defaultForEmptyMatch. */
export function getDefaultForKey(defaultKey: string): string {
  return SYSTEM_PROMPT_DEFAULTS[defaultKey] ?? '';
}
